/**
 * INCSUBPoolChat.jsx
 *
 * Component for displaying a pool-based chat interface.
 * Shows a chat window with messages from pool members and the host,
 * groups messages by date, and includes image/name/role context.
 *
 * @author Vishal Lohbande
 */

import React, { useEffect, useRef, useState} from 'react';
import './stylePoolChat.css';

/**
 * INCSUBPoolChat Component
 *
 * @component
 * @param {Object} props - Props object
 * @param {Object} props.poolChat - Contains all chat messages and metadata
 * @param {string} props.poolHost - Name of the pool host
 * @param {Function} props.setIsOpenChat - Function to toggle chat window visibility
 *
 * @returns {JSX.Element} The rendered chat UI
 */
function INCSUBPoolChat({ poolChat, poolHost, setIsOpenChat, poolName, poolImage  }) {
    const [chatList, setChatList] = useState(poolChat?.chatsMessagesDtoList);

    // Ref to scroll to bottom of chat on updates
    const bottomRef = useRef(null);
    const endOfMessagesRef = useRef(null);

    /**
     * Effect: Scrolls chat view to bottom when chatList updates
     * Ensures newest messages are visible
     *
     * @author vishal lohbande
     */
    useEffect(() => {
        if (endOfMessagesRef.current) {
            endOfMessagesRef.current.scrollIntoView({ behavior: 'auto' });
        }
    }, [chatList]);

    /**
     * Formats a date string to 'DD Month YYYY' format (e.g., 15 May 2025)
     *
     * @function
     * @param {string} dateString - Date string to format
     * @returns {string} Formatted date
     *
     * @author vishal lohbande
     */
    const formateDate = (dateString) => {
        const date = new Date(dateString);
        return new Intl.DateTimeFormat("en-GB", {
            day: "2-digit",
            month: 'long',
            year: "numeric"
        }).format(date);
    };

    return (
        <div className='INCSUBPoolChat' onClick={() => setIsOpenChat(false)}>
            <div className="chat-container" onClick={(e) => e.stopPropagation()}>
                {/* Header */}
                <div className="chat-header">
                    <img src={poolImage?poolImage:'/'} alt="Netflix Logo" />
                    <h1>{poolName}</h1>
                </div>

                {/* Chat Body */}
                <div className="chat-body" ref={bottomRef}>
                    {chatList && chatList.length > 0 && chatList.map((chat, index) =>
                        <div
                            className='message_holder'
                            ref={index === chatList.length - 1 ? endOfMessagesRef : null}
                            key={index}
                        >
                            {/* Render date header if first message or date changes */}
                            {(index === 0 || formateDate(chat.messageSentAt) !== formateDate(chatList[index - 1].messageSentAt)) && (
                                <div className="date-label">{formateDate(chat.messageSentAt)}</div>
                            )}

                            {/* Regular member message */}
                            {!chat.messageFor ? (
                                <div className="message member">
                                    <div className='image_holder'>
                                        <img src={chat.senderImage || "https://via.placeholder.com/30"} alt="User" />
                                    </div>
                                    <div className='message_info_container'>
                                        <div className='message_sender_name'>
                                            {chat.senderName !== 'Host' ? chat.senderName : poolHost}
                                            <span>{chat.senderName !== 'Host' ? "Member" : "Host"}</span>
                                        </div>
                                        <div className='text_container'>
                                            <div className="text">{chat?.chatMessage}</div>
                                            <div className='message_time'>22:05</div>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                // Message that replies to another user (likely host)
                                <div className="message host">
                                    <div className='image_holder'>
                                        <img src={chat.senderImage || "https://via.placeholder.com/30"} alt="User" />
                                    </div>
                                    <div className='message_info_container'>
                                        <div className='message_sender_name'>
                                            {chat.senderName !== 'Host' ? chat.senderName : poolHost}
                                            <span>{chat.senderName !== 'Host' ? "Member" : "Host"}</span>
                                        </div>
                                        <div className='text_container'>
                                            <div className='reply_on_chat'>
                                                <div className='message_replyed_name'>
                                                    <img
                                                        src={chat.replyOnUserImage || '/assets/icons/common/defaultImage.png'}
                                                        alt='sender'
                                                    />
                                                    {chat?.replyOnUserName}
                                                </div>
                                                <div className="text">{chat?.chatMessage}</div>
                                            </div>
                                            <div className='message_time'>22:05</div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* No chat case */}
                    {!chatList || chatList.length < 1 && (
                        <div className='INCSUBChat_empty'>No Chat Available</div>
                    )}

                    {/* Auto-scroll anchor */}
                    <div ref={endOfMessagesRef} />
                </div>
            </div>
        </div>
    );
}

export default INCSUBPoolChat;
